import { useState, useEffect, useRef } from 'react'
import { AiOutlineSearch, AiOutlineClose } from 'react-icons/ai'
import { motion, AnimatePresence } from 'framer-motion'

export default function SearchIcon ({isScroll}) {
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const searchInputRef = useRef(null)
  const searchContainerRef = useRef(null)

  // Focus input when search opens
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 300) // Wait for animation to complete
    }
  }, [isSearchOpen])

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target) &&
        isSearchOpen
      ) {
        setIsSearchOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isSearchOpen])

  const handleSearchSubmit = async () => {
    if (!searchText.trim()) return

  
    const searchParams = new URLSearchParams({ query: searchText })
    const redirectUrl = `/search-results?${searchParams.toString()}`
    window.location.href = redirectUrl
  }

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      handleSearchSubmit()
    }
  }

  const clearSearch = () => {
    setSearchText('')
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen)
    if (isSearchOpen) {
      setSearchText('')
    }
  }

  return (
    <div ref={searchContainerRef} className='relative'>
      {/* Search/Close Icon Button */}
      <motion.button
        onClick={toggleSearch}
        className={`p-3 rounded-lg hover:bg-slate-300/20 hover:text-gray-900 transition-all ${isScroll ? 'text-black' : 'text-white'}`}
        aria-label={isSearchOpen ? 'Close search' : 'Open search'}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <motion.div
          initial={false}
          animate={{ rotate: isSearchOpen ? 90 : 0 }}
          transition={{ duration: 0.2 }}
        >
          {isSearchOpen ? (
            <AiOutlineClose className='w-6 h-6 ' />
          ) : (
            <AiOutlineSearch className='w-6 h-6 ' />
          )}
        </motion.div>
      </motion.button>

      {/* Animated Search Bar */}
      <AnimatePresence>
        {isSearchOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{
              duration: 0.3,
              ease: [0.4, 0.0, 0.2, 1]
            }}
            className='absolute top-full right-0 mt-3 w-[90vw] sm:w-[500px] md:w-[600px] lg:w-[700px] max-w-4xl z-50'
            style={{
              transform: 'translateX(calc(50% + 24px))' // Center relative to the button
            }}
          >
            <div className='bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden'>
              <div className='flex items-center px-6 py-4'>
                {/* Search Input */}
                <div className='flex-1 flex items-center'>
                  <input
                    ref={searchInputRef}
                    type='text'
                    placeholder='Enter Your Search'
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className='w-full bg-transparent outline-none text-gray-800 placeholder-gray-400 text-lg font-medium'
                    disabled={isLoading}
                  />
                </div>

                {/* Clear Button (Red X) - Only show when input has text */}
                <AnimatePresence>
                  {searchText && (
                    <motion.button
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.2 }}
                      onClick={clearSearch}
                      className='p-2 bg-red-500 hover:bg-red-600 rounded-full transition-colors mr-3'
                      aria-label='Clear search'
                    >
                      <AiOutlineClose className='w-5 h-5 text-white' />
                    </motion.button>
                  )}
                </AnimatePresence>

                {/* Search Button */}
                <motion.button
                  onClick={handleSearchSubmit}
                  disabled={isLoading || !searchText.trim()}
                  className='  rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                  aria-label='Search'
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {isLoading ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: 'linear'
                      }}
                      
                    />
                  ) : (
                    <AiOutlineSearch className='' size={40} />
                  )}
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
